import React, { useMemo } from "react";
import { Button, Typography } from "antd";
import { useTranslation } from "react-i18next";
import ScheduledActionList from "../../../components/scheduledActions/ScheduledActionList";
import TimedActionEditModal from "../../../components/scheduledActions/TimedActionEditModal";
import { useTimedActionsCrud } from "../../../components/scheduledActions/useTimedActionsCrud";
import { useScheduledActionPermissions } from "../../../components/scheduledActions/useScheduledActionPermissions";
import {
  useControlOptions,
  TIMEZONE_DEFAULT,
} from "../../../components/scheduledActions/scheduledActionDefaults";

const { Text } = Typography;

interface ControlPanelLiftScheduledSectionProps {
  liftId: string;
  bankId: string;
  organizationId: string;
}

function ControlPanelLiftScheduledSection({
  liftId,
  bankId,
  organizationId,
}: ControlPanelLiftScheduledSectionProps): React.ReactElement | null {
  const [t] = useTranslation("global");
  const { canView, canCreate, canUpdate, canDelete } = useScheduledActionPermissions();

  const {
    actions: allBankActions,
    isFetching,
    fetchError,
    submitting,
    modalOpen,
    editDraft,
    closeModal,
    handleCreate,
    handleEdit,
    handleSubmit,
    handleDelete,
    handleToggleEnabled,
    alerts,
  } = useTimedActionsCrud({ kind: "lift", organizationId, bankId, liftId }, { enabled: canView });

  const controlOptions = useControlOptions();

  // Split: lift-specific vs bank-wide (lift_id undefined/empty)
  const liftActions = useMemo(
    () => allBankActions.filter((a) => a.lift_id === liftId),
    [allBankActions, liftId],
  );
  const bankWideActions = useMemo(
    () => allBankActions.filter((a) => a.lift_id === undefined || a.lift_id === ""),
    [allBankActions],
  );

  if (!canView) {
    return null;
  }

  return (
    <div>
      {canCreate && (
        <div style={{ marginBottom: 8 }}>
          <Button size="small" type="primary" onClick={() => handleCreate()}>
            {t("scheduledActions.addAction", "Add Scheduled Action")}
          </Button>
        </div>
      )}

      {bankWideActions.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Text
            type="secondary"
            style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            {t("scheduledActions.bankWideActions", "Bank-wide actions")}
          </Text>
          <ScheduledActionList
            actions={bankWideActions}
            loading={false}
            onEdit={canUpdate ? handleEdit : undefined}
            onDelete={canDelete ? handleDelete : undefined}
            controlOptions={controlOptions}
          />
        </div>
      )}

      <ScheduledActionList
        actions={liftActions}
        loading={isFetching}
        error={fetchError}
        onEdit={canUpdate ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        onToggleEnabled={canUpdate ? handleToggleEnabled : undefined}
        controlOptions={controlOptions}
      />

      <TimedActionEditModal
        editDraft={editDraft}
        open={modalOpen}
        controlOptions={controlOptions}
        timezoneDefault={TIMEZONE_DEFAULT}
        submitting={submitting}
        onSubmit={handleSubmit}
        onCancel={closeModal}
      />

      {alerts}
    </div>
  );
}

export default ControlPanelLiftScheduledSection;
